/*!
 * Cart discount-code input handler.
 *
 * Loaded once on every page from snippets/cart-drawer.liquid so the
 * document-level click delegation is registered before the customer ever
 * interacts with the cart. The discount input markup itself lives in
 * snippets/cart-discount-input.liquid; that snippet may be injected late
 * (e.g. when cart-drawer's renderContents() swaps in fresh HTML after add-
 * to-cart) and browsers do NOT execute <script> tags introduced via
 * innerHTML, so keeping the JS inline there meant the very first auto-
 * opened drawer had no working Anvend button. Hosting the script as an
 * external asset that always loads with the layout fixes that.
 */
(function () {
  if (window.__mifaDiscountBoundOnce) return;
  window.__mifaDiscountBoundOnce = true;

  async function cartPageRefresh(value) {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { discount_code: value || '' } }),
    }).then(function (r) {
      if (!r.ok) throw new Error('Cart update failed: ' + r.status);
    });
    window.location.reload();
  }

  async function drawerRefresh(value) {
    const drawer = document.querySelector('cart-drawer');
    const wasDrawerOpen = !!drawer && (
      drawer.classList.contains('active') ||
      drawer.classList.contains('is-open') ||
      drawer.hasAttribute('open') ||
      document.body.classList.contains('cart-drawer-open')
    );
    const sectionIds = drawer && typeof drawer.getSectionsToRender === 'function' && drawer.getSectionsToRender().length
      ? drawer.getSectionsToRender().map(function (s) { return s.id; })
      : ['cart-drawer', 'cart-icon-bubble'];
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { discount_code: value || '' },
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error('Cart update failed: ' + res.status);
    const parsedState = await res.json();

    if (drawer && typeof drawer.renderContents === 'function' && parsedState.sections) {
      try { drawer.renderContents(parsedState); } catch (e) { /* fall through */ }
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mifa-discount', cartData: parsedState });
    }
    // Re-trigger the drawer's slide-in animation; renderContents wipes the
    // open-state classes and the panel rebounds to translateX(500px).
    if (wasDrawerOpen) {
      const drawerNow = document.querySelector('cart-drawer');
      if (drawerNow && typeof drawerNow.toggleDrawerClasses === 'function') {
        try { drawerNow.toggleDrawerClasses(true); } catch (e) { /* fall through */ }
      } else if (drawerNow) {
        drawerNow.classList.add('active', 'is-open', 'open', 'animate');
        drawerNow.setAttribute('open', '');
      }
      document.body.classList.add('cart-drawer-open');
    }
    return parsedState;
  }

  function refreshFor(wrapper, value) {
    return wrapper.closest('cart-drawer') ? drawerRefresh(value) : cartPageRefresh(value);
  }

  function showStatus(wrapper, msg, kind) {
    const status = wrapper.querySelector('[data-discount-status]');
    const input = wrapper.querySelector('[data-discount-input]');
    if (!status) return;
    status.removeAttribute('data-error');
    status.removeAttribute('data-applied');
    let iconHtml = '';
    if (kind === 'applied') iconHtml = '<span class="calculator-discount__icon" aria-hidden="true">✓</span>';
    status.innerHTML = iconHtml + (msg || '').replace(/</g, '&lt;');
    if (kind === 'error') status.setAttribute('data-error', '');
    if (kind === 'applied') status.setAttribute('data-applied', '');
    if (input) {
      if (kind === 'error') input.setAttribute('data-input-state', 'error');
      else if (kind === 'applied') input.setAttribute('data-input-state', 'applied');
      else input.removeAttribute('data-input-state');
    }
  }

  async function handleApply(applyBtn) {
    const wrapper = applyBtn.closest('[data-discount-input-wrapper]');
    if (!wrapper) return;
    const input = wrapper.querySelector('[data-discount-input]');
    const code = (input?.value || '').trim().toUpperCase();
    if (!code) { showStatus(wrapper, 'Indtast en rabatkode.', 'error'); return; }
    applyBtn.disabled = true;
    showStatus(wrapper, 'Validerer rabatkoden…');

    let validation;
    try {
      const res = await fetch('https://mifa-backend.netlify.app/.netlify/functions/validate-discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      validation = await res.json();
      if (!res.ok && !validation?.error) throw new Error('validate-discount HTTP ' + res.status);
    } catch (e) {
      // Network / server fault — degrade gracefully; checkout-time validation
      // in create-order.js still catches bad codes.
      showStatus(wrapper, 'Kunne ikke validere rabatkoden lige nu — den prøves ved checkout.');
      try { await refreshFor(wrapper, code); }
      catch (e2) { showStatus(wrapper, 'Kunne ikke gemme rabatkode. Prøv igen.', 'error'); applyBtn.disabled = false; }
      return;
    }

    if (!validation.ok) {
      showStatus(wrapper, validation.error || 'Rabatkoden er ugyldig.', 'error');
      applyBtn.disabled = false;
      return;
    }

    try {
      await refreshFor(wrapper, code);
    } catch (e) {
      showStatus(wrapper, 'Kunne ikke gemme rabatkode. Prøv igen.', 'error');
      applyBtn.disabled = false;
    }
  }

  async function handleRemove(removeBtn) {
    const wrapper = removeBtn.closest('[data-discount-input-wrapper]');
    if (!wrapper) return;
    removeBtn.disabled = true;
    showStatus(wrapper, 'Fjerner rabatkode…');
    try { await refreshFor(wrapper, ''); }
    catch (e) { showStatus(wrapper, 'Kunne ikke fjerne rabatkode. Prøv igen.', 'error'); removeBtn.disabled = false; }
  }

  document.addEventListener('click', function (e) {
    const apply = e.target.closest('[data-discount-apply]');
    if (apply) { handleApply(apply); return; }
    const remove = e.target.closest('[data-discount-remove]');
    if (remove) { handleRemove(remove); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('[data-discount-input]');
    if (!input) return;
    e.preventDefault();
    const apply = input.closest('[data-discount-input-wrapper]')?.querySelector('[data-discount-apply]');
    apply && handleApply(apply);
  });
})();
